import * as React from 'react';
import { useCallback, useMemo } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import { Divider, ListItemIcon, ListItemText } from '@mui/material';
import GroupSharpIcon from '@mui/icons-material/GroupSharp';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import PersonSharpIcon from '@mui/icons-material/PersonSharp';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@mui/styles';
import { CollectorScope, CollectorSettingScope } from 'graphql/types';
import { useMeContext } from 'modules/me';

const useStyles = makeStyles((theme) => ({
  menuIcon: {
    '&.MuiListItemIcon-root': {
      color: theme.palette.common.black,
      minWidth: theme.spacing(4)
    }
  },

  paper: {
    opacity: 1
  },

  menu: {
    top: theme.spacing(0.5),
    left: ({ installationScopeSet }: { installationScopeSet: Set<string> }) =>
      installationScopeSet.has(CollectorScope.Tenant)
        ? theme.spacing(2.4)
        : theme.spacing(0.5)
  },
  menuListstyle: {
    padding: theme.spacing(0),
    backgroundColor: theme.palette.information.pageBackground
  },
  menuItem: {
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.5),
    minWidth: theme.spacing(15.5)
  },
  MenuDivider: {
    margin: theme.spacing(0)
  },
  forwardIcon: {
    paddingLeft: theme.spacing(1.7),
    color: theme.palette.common.black,
    fontSize: theme.spacing(3.6)
  }
}));

const useSubMenuStyles = makeStyles((theme) => ({
  menuItem: {
    width: ({ isSubmenuOpen }: { isSubmenuOpen: boolean }) =>
      isSubmenuOpen ? theme.spacing(15) : theme.spacing(0),
    minHeight: theme.spacing(0),
    pointerEvents: 'auto'
  },

  innerMenu: {
    pointerEvents: 'none',
    marginLeft: theme.spacing(-0.3)
  },
  menuListstyle: {
    padding: ({ isSubmenuOpen }: { isSubmenuOpen: boolean }) =>
      isSubmenuOpen ? theme.spacing(0.3) : theme.spacing(0),
    backgroundColor: theme.palette.information.pageBackground,
    minWidth: theme.spacing(0),
    minHeight: theme.spacing(0)
  }
}));

const usePopOverSubMenuStyle = makeStyles((theme) => ({
  paper: {
    minWidth: theme.spacing(0),
    minHeight: theme.spacing(0)
  }
}));

type MenuPopperProp = {
  menuItem: {
    name: string;
    onClick: () => void;
    testId: string;
  }[];
  anchorPopper: React.RefObject<HTMLLIElement>;
  anchorMenu: HTMLElement | null;
  handleButtonClose: () => void;
  onMouseOverCompany: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseOverUser: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
  scopeSet: Set<string>;
  installationScopeSet: Set<string>;
  connectClick: (scope: any) => void;
  openTenant: boolean;
  menuAnchorUserE1: React.RefObject<HTMLLIElement>;
  onMouseLeaveUser: () => void;
  openUser: boolean;
};
type subMenuProp = {
  anchor: any;
  isSubmenuOpen: boolean;
  onMouseLeave: () => void;
  menuItem: {
    name: string;
    onClick: () => void;
    testId: string;
  }[];
};
export const SubMenu: React.FC<subMenuProp> = ({
  anchor,
  isSubmenuOpen,
  onMouseLeave,
  menuItem
}: subMenuProp) => {
  const classes = useSubMenuStyles({ isSubmenuOpen });
  return (
    <Menu
      style={{ pointerEvents: 'none' }}
      elevation={2}
      anchorEl={anchor.current}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left'
      }}
      open={isSubmenuOpen}
      MenuListProps={{ className: classes.menuListstyle }}
      className={classes.innerMenu}
      onClose={onMouseLeave}
      PopoverClasses={usePopOverSubMenuStyle()}
    >
      <div className={classes.menuItem}>
        {menuItem.map((i) => (
          <MenuItem
            className={classes.menuItem}
            key={i.name}
            data-testid={i.testId}
            onClick={i.onClick}
          >
            {i.name}
          </MenuItem>
        ))}
      </div>
    </Menu>
  );
};

export const MenuPopper: React.FC<MenuPopperProp> = ({
  menuItem,
  anchorPopper,
  anchorMenu,
  handleButtonClose,
  onMouseOverCompany,
  onMouseLeave,
  scopeSet,
  installationScopeSet,
  connectClick,
  openTenant,
  menuAnchorUserE1,
  onMouseLeaveUser,
  openUser,
  onMouseOverUser
}: MenuPopperProp) => {
  const isSubmenuTenantOpen = useMemo(() => openTenant && Boolean(anchorMenu), [
    anchorMenu,
    openTenant
  ]);
  const isSubmenuUserOpen = useMemo(() => openUser && Boolean(anchorMenu), [
    anchorMenu,
    openUser
  ]);
  const classes = useStyles({
    installationScopeSet
  });
  const isTenantInstalled = React.useMemo(
    () => installationScopeSet.has(CollectorSettingScope.Tenant),
    [installationScopeSet]
  );
  const me = useMeContext();
  const isAdmin = useMemo(() => {
    return me?.permissions.hasCompanyCollectorInstallAccess ? true : false;
  }, [me]);
  const iscollectorTenant = React.useMemo(
    () => scopeSet.has(CollectorSettingScope.Tenant) && isAdmin,
    [isAdmin, scopeSet]
  );
  const isUserInstalled = React.useMemo(
    () => installationScopeSet.has(CollectorSettingScope.User),
    [installationScopeSet]
  );
  const iscollectorUser = React.useMemo(
    () => scopeSet.has(CollectorSettingScope.User),
    [scopeSet]
  );

  const onTenantClick = useCallback(() => {
    if (!isTenantInstalled && !isUserInstalled)
      connectClick(CollectorSettingScope.Tenant);
  }, [connectClick, isTenantInstalled, isUserInstalled]);

  const onUserClick = useCallback(() => {
    if (!isTenantInstalled && !isUserInstalled)
      connectClick(CollectorSettingScope.User);
  }, [connectClick, isTenantInstalled, isUserInstalled]);

  return (
    <div>
      <Menu
        anchorEl={anchorMenu}
        open={Boolean(anchorMenu)}
        MenuListProps={{ className: classes.menuListstyle }}
        onClose={handleButtonClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        elevation={1}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        className={classes.menu}
        data-testid="modify-menu"
      >
        {iscollectorTenant && (
          <div
            onMouseEnter={onMouseOverCompany}
            onMouseLeave={onMouseLeave}
            data-testid={'collector-button'}
          >
            <MenuItem
              data-testid={`collector-button-${CollectorScope.Tenant}`}
              className={classes.menuItem}
              ref={anchorPopper}
              onClick={onTenantClick}
            >
              <ListItemIcon className={classes.menuIcon}>
                <GroupSharpIcon />
              </ListItemIcon>
              <ListItemText>
                <FormattedMessage id="collectorType.company" />
              </ListItemText>
              {installationScopeSet.size > 0 && (
                <ListItemIcon className={classes.menuIcon}>
                  <ArrowForwardIosSharpIcon className={classes.forwardIcon} />
                </ListItemIcon>
              )}
            </MenuItem>

            <SubMenu
              anchor={anchorPopper}
              menuItem={menuItem}
              isSubmenuOpen={isSubmenuTenantOpen}
              onMouseLeave={onMouseLeave}
            />
          </div>
        )}
        {scopeSet.size > 1 && <Divider className={classes.MenuDivider} />}
        {iscollectorUser && (
          <div
            onMouseLeave={onMouseLeaveUser}
            onMouseEnter={onMouseOverUser}
            data-testid={'menu-button-user'}
          >
            <MenuItem
              data-testid={`collector-button-${CollectorScope.User}`}
              className={classes.menuItem}
              onClick={onUserClick}
              ref={menuAnchorUserE1}
            >
              <ListItemIcon className={classes.menuIcon}>
                <PersonSharpIcon />
              </ListItemIcon>
              <ListItemText>
                <FormattedMessage id="collectorType.self" />
              </ListItemText>
              {installationScopeSet.size > 0 && (
                <ListItemIcon className={classes.menuIcon}>
                  <ArrowForwardIosSharpIcon className={classes.forwardIcon} />
                </ListItemIcon>
              )}
            </MenuItem>

            <SubMenu
              anchor={menuAnchorUserE1}
              menuItem={menuItem}
              isSubmenuOpen={isSubmenuUserOpen}
              onMouseLeave={onMouseLeaveUser}
            />
          </div>
        )}
      </Menu>
    </div>
  );
};
